import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs, { Dayjs } from 'dayjs'

export type DashboardFilter = {
  province_1?: any,
  district_1?: any,
  ward_1?: any,
  province_2?: any,
  ward_2?: any,
  school_id?: any,
  class_id?: any,
  start_date?: Dayjs,
  end_date?: Dayjs
}

export interface DashboardChartType {
  school_id: number;
  school_name: string;
  student_count: number;
  class_count: number;
  submodule_count: number;
  total_sub_give: number;
  total_sub_handle: number;
  sum_all_count_times: number;
  sum_all_pass_times: number;
}

export interface DashboardStatsType {
  total_school: number;
  total_class: number;
  total_student: number;
  total_submodule_count: number;
  total_sub_give: number;
  total_sub_handle: number;
  sum_all_count_times: number;
  sum_all_pass_times: number;
}

export interface DashboardResponse {
  totals?: DashboardStatsType;
  data?: DashboardChartType[];
}

type SchoolStudentCount = {
  school_id: number;
  school_name: string;
  student_count: number;
}

type SubmoduleStatsData = {
  percentage: number;
  total_students: number;
  completed_students: number;
}

type SubmoduleDetailData = {
  student_name: string;
  completion_rate: string;
}

type ModuleStatsData = {
  percentage: number;
  students: Array<{
    student_name: string;
    firstname: string;
    lastname: string;
    module_name: string;
    scores: Array<{
      score: number;
      max_score: number;
      percentage: number;
    }>;
  }>;
}

interface AdminDashboardState {
  loading: any;
  adminDashboardData: any;
  dashboardOverviewData: any;
  studentCountBySchools: any;
  submoduleStats: any;
  submoduleDetails: any;
  moduleStats: any;
  exerciseChartsLoading: any;
  subModuleDetailsChartsLoading: any;
  moduleStatsChartLoading: any;
  adminDashboardFilter: any;
  adminDashboardFilterSelected: any;
  dashboardFilter: DashboardFilter;
  dashboardResponse: DashboardResponse;
  dashboardClassResponse: any;
}

const initialState: AdminDashboardState = {
  loading: false,
  adminDashboardData: {},
  dashboardOverviewData: {
    schools: 0,
    classes: 0,
    students: 0,
    exercises: 0
  },
  studentCountBySchools: [] as SchoolStudentCount[],
  // Exercise chart data
  submoduleStats: null as SubmoduleStatsData | null,
  submoduleDetails: [] as SubmoduleDetailData[],
  moduleStats: null as ModuleStatsData | null,
  exerciseChartsLoading: false,
  subModuleDetailsChartsLoading: false,
  moduleStatsChartLoading: false,
  // filter options 
  adminDashboardFilter: {
    province: null,
    district: null,
    school_ids: null,
    class_ids: null,
    module_ids: null,
    user_ids: null,
    date_from: null,
    date_to: null
  },
  // filter selected 
  adminDashboardFilterSelected: {
    province: '',
    district: '',
    school_ids: '',
    class_ids: '',
    module_ids: '',
    user_ids: '',
    date_from: '',
    date_to: ''
  },
  dashboardFilter: {
    province_1: null,
    district_1: null,
    ward_1: null,
    province_2: null,
    ward_2: null,
    school_id: null,
    class_id: null,
    start_date: dayjs().startOf("month"),
    end_date: dayjs().endOf("month")
  },
  dashboardResponse: {},
  dashboardClassResponse: null
};

const adminDashboardSlice = createSlice({
  name: 'adminDashboard',
  initialState,
  reducers: {
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setAdminDashboardData(state, action) {
      state.adminDashboardData = action.payload;
    },
    setDashboardOverviewData(state, action) {
      state.dashboardOverviewData = action.payload;
    },
    setStudentCountBySchools(state, action: PayloadAction<SchoolStudentCount[]>) {
      state.studentCountBySchools = action.payload;
    },
    setAdminDashboardFilter(state, action) {
      if (Object.keys(action.payload)) {
        state.adminDashboardFilter = { ...state.adminDashboardFilter, ...action.payload };
      }
    },
    setAdminDashboardFilterSelected(state, action) {
      state.adminDashboardFilterSelected = action.payload;
    },
    setSubmoduleStats(state, action) {
      state.submoduleStats = action.payload;
    },
    setSubmoduleDetails(state, action) {
      state.submoduleDetails = action.payload;
    },
    setModuleStats(state, action) {
      state.moduleStats = action.payload;
    },
    setExerciseChartsLoading(state, action) {
      state.exerciseChartsLoading = action.payload;
    },
    setSubmoduleDetailsChartsLoading(state, action) {
      state.subModuleDetailsChartsLoading = action.payload;
    },
    setModuleStatsLoading(state, action) {
      state.moduleStatsChartLoading = action.payload;
    },
    clearSubModuleStats(state) {
      state.submoduleStats = null
    },
    clearModuleStats(state) {
      state.moduleStats = null
    },
    clearSubmoduleDetails(state) {
      state.submoduleDetails = []
    },
    setDashboardFilter(state, action: PayloadAction<DashboardFilter>) {
      state.dashboardFilter = {
        ...action.payload
      }
    },
    setDashboardResponse(state, action: PayloadAction<DashboardResponse>) {
      state.dashboardResponse = action.payload
    },
    setDashboardClassResponse(state, action) {
      state.dashboardClassResponse = action.payload
    },
    clearDashboardFilter(state, action: PayloadAction<{ school_id: any, class_id: any }>) {
      state.dashboardFilter = {
        province_1: null,
        district_1: null,
        ward_1: null,
        province_2: null,
        ward_2: null,
        school_id: action.payload?.school_id || null,
        class_id: action.payload?.class_id || null,
        start_date: dayjs().subtract(30, 'day'),
        end_date: dayjs()
      }
    }
  },
});

export const {
  setLoading,
  setAdminDashboardData,
  setDashboardOverviewData,
  setStudentCountBySchools,
  setAdminDashboardFilter,
  setAdminDashboardFilterSelected,
  setSubmoduleStats,
  setSubmoduleDetails,
  setModuleStats,
  setExerciseChartsLoading,
  setSubmoduleDetailsChartsLoading,
  setModuleStatsLoading,
  clearSubModuleStats,
  clearModuleStats,
  clearSubmoduleDetails,
  setDashboardFilter,
  clearDashboardFilter,
  setDashboardResponse,
  setDashboardClassResponse
} = adminDashboardSlice.actions;

export default adminDashboardSlice.reducer;
